import { Component, OnInit } from '@angular/core';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WebsocketService } from '../../services/websocket.service';
import { filter, take } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
	selector: 'app-scheduler',
	standalone: true,
	imports: [TranslatePipe, CommonModule, NgFor, NgIf, FormsModule, MatInputModule,
		MatButtonModule,
		NgxMaterialTimepickerModule],
	templateUrl: './scheduler.component.html',
	styleUrl: './scheduler.component.scss'
})
export class SchedulerComponent implements OnInit {
	mac: string | null = null;
	tasks: any[] = [];
	inputs: any[] = [];
	outputs: any[] = [];
	scenarios: any[] = [];
  deletingAction: { taskIndex: number, actionIndex: number } | null = null;
  addingAction: { taskIndex: number, actionIndex: number } | null = null;

	expandedTasks: Set<number> = new Set();

	constructor(private route: ActivatedRoute,
							private router: Router,
							private websocketService: WebsocketService) {}

	ngOnInit(): void {
		this.mac = this.route.snapshot.paramMap.get('mac');
		if (this.mac) {
			this.websocketService.getUserDevices().pipe(filter((devices: any) => devices != null), take(1))
				.subscribe((devices) => {
					const device = devices.find((dev: any) => dev.mac === this.mac);
					if (device) {												
						this.tasks = device?.config.tasks || [];
						this.inputs = (device?.config.inputs || []).filter((input: any) => input.id < 16);
						this.outputs = device?.config.outputs || [];
						this.scenarios = device?.config.scenarios || [];
            console.log('inputs ', this.inputs);
					}
				});
		}
	}

	trackByTask(index: number, item: any) {
		return item?.id ?? index;
	}

	toggleExpand(task: any) {
		const id = task.id;
		if (this.expandedTasks.has(id)) this.expandedTasks.delete(id); else this.expandedTasks.add(id);
	}

	isExpanded(task: any): boolean {
		return this.expandedTasks.has(task.id);
	}

	back() {
		setTimeout(() => {
			const fromTab = (window.history && (window.history.state as any)?.fromTab) ? (window.history.state as any).fromTab : undefined;
			if (fromTab) {
				this.router.navigate(['/controller', this.mac], { state: { activeTab: fromTab } });
			} else {
				this.router.navigate(['/controller', this.mac]);
			}
		}, 150);
	}

	addTask() {
		const newId = Math.max(0, ...(this.tasks?.map(t => t.id) || [])) + 1;
		const newTask = {
			id: newId,
			name: `Task ${newId}`,
			enabled: true,
			done: false,
			time: 0,
			grace: 0,
			dow: [],
			actions: []
		};
		this.tasks = [...this.tasks, newTask];		
		this.expandedTasks.add(newId);
	}

	deleteTask(task: any) {
		this.tasks = this.tasks.filter(t => t.id !== task.id);
		this.expandedTasks.delete(task.id);
	}

	addAction(taskIndex: number): void {
    if (!this.tasks) return;
    const actions = this.tasks[taskIndex].actions;    
    
    actions.push({      
      action: 'on'
    });

    this.addingAction = { taskIndex, actionIndex: actions.length - 1 };
    setTimeout(() => {
      this.addingAction = null;
    }, 300); // Match this with the CSS animation duration
  }

	toggleDow(task: any, day: number) {
		if (!Array.isArray(task.dow)) task.dow = [];
		const idx = task.dow.indexOf(day);
		if (idx > -1) task.dow.splice(idx, 1); else { task.dow.push(day); task.dow.sort(); }
	}

	updateTaskTime(task: any, value: string) {
		const [h, m] = value.split(':').map(Number);
	    task.time = h * 60 + m;
	}

	save() {		
		// ensure scheduler reference    
		this.websocketService.sendMessage({ 
        type: 'UPDATESCHEDULER', 
        payload: { mac: this.mac, tasks: this.tasks },
      });    
//console.log(this.tasks);
		this.back();
	}

	// Translation keys, piped in the template, so a language switch repaints them.
	dayNames: Record<number, string> = {
		0: 'common.dow.sunday',
		1: 'common.dow.monday',
		2: 'common.dow.tuesday',
		3: 'common.dow.wednesday',
		4: 'common.dow.thursday',
		5: 'common.dow.friday',
		6: 'common.dow.saturday'
	};

	formatTime(minutes: number | undefined): string {
		if (minutes === undefined || minutes === null) {
			return '';
		}
		const h = Math.floor(minutes / 60)
			.toString()
			.padStart(2, '0');
		const m = (minutes % 60)
			.toString()
			.padStart(2, '0');
		return `${h}:${m}`;
	}
  
  removeAction(taskIndex: number, actionIndex: number): void {
    this.deletingAction = { taskIndex, actionIndex };
    setTimeout(() => {
      this.tasks[taskIndex].actions.splice(actionIndex, 1);
      this.deletingAction = null;
    }, 300); // Match this with the CSS animation duration
  }
  
  isDeleting(taskIndex: number, actionIndex: number): boolean {
    return this.deletingAction?.taskIndex === taskIndex && 
           this.deletingAction?.actionIndex === actionIndex;
  }

  isAdding(taskIndex: number, actionIndex: number): boolean {
    return this.addingAction?.taskIndex === taskIndex && 
           this.addingAction?.actionIndex === actionIndex;
  }
}